import * as React from 'react';
import {
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceArea,
  Tooltip,
  ResponsiveContainer,
  Area,
  Legend,
} from 'recharts';
import { cn } from '../../lib/cn';
import { useReducedMotion } from '../../hooks/use-reduced-motion';

/**
 * Big trend chart — GA-style. Per `01-build-brief.md` §5:
 *   - period ≤ 1 day  → hourly buckets
 *   - period 2–14 d   → daily buckets
 *   - period > 14 d   → daily, with optional weekly average overlay
 *   - prior-period dashed comparison line
 *
 * Consumer passes already-bucketed data. Granularity is informational
 * (sets the X-axis tick formatter).
 */

export type TrendGranularity = 'hourly' | 'daily';

export interface TrendPoint {
  /** ISO timestamp or display string for the bucket. */
  bucket: string;
  /** Primary metric value. */
  value: number;
  /** Prior-period value for the same bucket index. */
  prev?: number;
}

export interface TrendChartProps {
  data: TrendPoint[];
  granularity: TrendGranularity;
  /** Y-axis number formatter. Default: `Intl.NumberFormat`. */
  formatValue?: (n: number) => string;
  /** Hide the prior-period comparison line. Default: show. */
  hideComparison?: boolean;
  /** Empty-state message when data is empty. */
  emptyMessage?: string;
  /**
   * Bucket key (must equal one `TrendPoint.bucket`) that is still
   * accumulating. Opt-in: the segment leading into it is hatched, its tooltip
   * is labelled "(incomplete)", and `partialNote` is captioned underneath.
   * Callers must also keep it out of any delta they compute — this component
   * only draws, it does not aggregate. Use `isPartialBucket` to derive it.
   */
  partialBucket?: string;
  /** Caption under the chart explaining how incomplete the bucket is. */
  partialNote?: string | null;
  className?: string;
  height?: number;
}

const DEFAULT_HEIGHT = 320;

const fmtNum = (n: number): string =>
  new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(n);

const tickFormatter = (granularity: TrendGranularity) => (value: string) => {
  if (granularity === 'hourly') {
    // Try to parse as a "YYYY-MM-DD HH" or hour bucket.
    const m = /(\d{1,2})(?::|h)?$/.exec(value);
    if (m) return `${m[1]}h`;
  }
  // Otherwise — just keep MM-DD or whatever the bucket already is.
  if (/^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(5);
  return value;
};

export function TrendChart({
  data,
  granularity,
  formatValue = fmtNum,
  hideComparison = false,
  emptyMessage = 'No data for this period.',
  partialBucket,
  partialNote,
  className,
  height = DEFAULT_HEIGHT,
}: TrendChartProps) {
  // The fill Area is a path, not a Bar rectangle, so it renders at t=0
  // rather than sitting empty until a frame runs — but it still animates
  // regardless of `prefers-reduced-motion`. `--duration-chart: 0ms`
  // (packages/tokens/src/tokens.css) promises otherwise; recharts reads
  // duration from a JS prop, never the CSS variable, so this is the bridge.
  const reduceMotion = useReducedMotion();

  if (!data || data.length === 0) {
    return (
      <div
        className={cn(
          'flex items-center justify-center rounded-lg border border-dashed border-border bg-bg-surface text-text-muted',
          className,
        )}
        style={{ height }}
      >
        {emptyMessage}
      </div>
    );
  }

  // The partial bucket is a band on a category axis, so it needs a
  // neighbouring tick to span. When it is the only point there is nothing to
  // shade — the caption and the tooltip label still carry the fact.
  const partialIndex = partialBucket ? data.findIndex((d) => d.bucket === partialBucket) : -1;

  return (
    <div className={cn('rounded-lg border border-border bg-bg-surface p-4', className)}>
      {/* THE SAME NUMBERS, READABLE WITHOUT THE CHART.
          Recharts draws to SVG and its tooltip is pointer-driven, so a screen-reader or
          keyboard-only user gets nothing at all from the picture. Replacing a textual list with
          this component therefore REMOVED information for exactly the people who had the least of
          it. This table carries every bucket and value, is announced normally, and costs sighted
          users nothing. `aria-hidden` on the chart stops the same data being announced twice. */}
      <table className="sr-only">
        <caption>Trend data, {granularity}</caption>
        <thead>
          <tr>
            <th scope="col">Period</th>
            <th scope="col">Value</th>
            {data.some((d) => d.prev !== undefined) && <th scope="col">Prior period</th>}
          </tr>
        </thead>
        <tbody>
          {data.map((d) => (
            <tr key={d.bucket}>
              <th scope="row">
                {d.bucket}
                {d.bucket === partialBucket ? ' (incomplete)' : ''}
              </th>
              <td>{formatValue(d.value)}</td>
              {data.some((x) => x.prev !== undefined) && (
                <td>{d.prev === undefined ? 'not available' : formatValue(d.prev)}</td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      <div aria-hidden="true">
      <ResponsiveContainer width="100%" height={height}>
        <ComposedChart data={data} margin={{ top: 8, right: 12, left: 4, bottom: 0 }}>
          <defs>
            <linearGradient id="trend-fill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--accent-500)" stopOpacity={0.18} />
              <stop offset="100%" stopColor="var(--accent-500)" stopOpacity={0} />
            </linearGradient>
            {/* Hatch, not a tint: it survives both themes and reads without
                colour perception, which a lighter fill alone does not. */}
            <pattern
              id="trend-partial-hatch"
              width={6}
              height={6}
              patternUnits="userSpaceOnUse"
              patternTransform="rotate(45)"
            >
              <line x1={0} y1={0} x2={0} y2={6} stroke="var(--border-strong)" strokeWidth={2} />
            </pattern>
          </defs>
          <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" vertical={false} />
          {partialIndex > 0 && (
            <ReferenceArea
              x1={data[partialIndex - 1]!.bucket}
              x2={data[partialIndex]!.bucket}
              fill="url(#trend-partial-hatch)"
              fillOpacity={0.45}
              stroke="none"
            />
          )}
          <XAxis
            dataKey="bucket"
            tick={{ fill: 'var(--text-muted)', fontSize: 11 }}
            tickFormatter={tickFormatter(granularity)}
            tickLine={false}
            axisLine={{ stroke: 'var(--border)' }}
          />
          <YAxis
            tick={{ fill: 'var(--text-muted)', fontSize: 11 }}
            tickFormatter={formatValue}
            tickLine={false}
            axisLine={{ stroke: 'var(--border)' }}
            width={56}
          />
          <Tooltip
            cursor={{ stroke: 'var(--border-strong)', strokeDasharray: '3 3' }}
            contentStyle={{
              backgroundColor: 'var(--bg-surface)',
              border: '1px solid var(--border)',
              borderRadius: 8,
              fontSize: 12,
              color: 'var(--text-primary)',
            }}
            formatter={(value: number) => formatValue(value)}
            labelFormatter={(label: string) =>
              label === partialBucket ? `${label} (incomplete)` : label
            }
          />
          <Legend
            verticalAlign="top"
            height={28}
            wrapperStyle={{ fontSize: 12, color: 'var(--text-secondary)' }}
            iconSize={10}
          />
          <Area
            type="monotone"
            dataKey="value"
            name="Period"
            stroke="var(--accent-500)"
            strokeWidth={2}
            fill="url(#trend-fill)"
            isAnimationActive={!reduceMotion}
            animationDuration={600}
          />
          <Line
            type="monotone"
            dataKey="value"
            name="Period"
            stroke="var(--accent-500)"
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4, stroke: 'var(--accent-500)', fill: 'var(--bg-surface)' }}
            legendType="none"
            isAnimationActive={false}
          />
          {!hideComparison && (
            <Line
              type="monotone"
              dataKey="prev"
              name="Prior period"
              stroke="var(--text-muted)"
              strokeWidth={1.5}
              strokeDasharray="4 4"
              dot={false}
              activeDot={{ r: 3, stroke: 'var(--text-muted)', fill: 'var(--bg-surface)' }}
              isAnimationActive={false}
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
      </div>
      {/* text-text-secondary, not text-text-muted: this caption is the
          load-bearing explanation that stops a partial bucket being read as a
          real collapse. The muted token sits below the 4.5:1 small-text floor
          in the light theme and the design contract restricts it to decorative
          or large text. */}
      {partialNote ? <p className="mt-2 text-caption text-text-secondary">{partialNote}</p> : null}
    </div>
  );
}
